<template>
    <div v-if="show" class="fixed inset-0 z-[200] flex items-center justify-center p-4">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" @click="$emit('close')"></div>

        <!-- Modal Content -->
        <div class="relative bg-[#202124] w-full max-w-sm rounded-[32px] shadow-2xl overflow-hidden animate-in zoom-in duration-300 border border-white/5">
            <!-- Header -->
            <div class="px-6 py-5 border-b border-white/5 flex items-center justify-between">
                <h3 class="text-white text-base font-medium">{{ reactions.length }} reactions</h3>
            </div>

            <!-- Emoji Action Bar -->
            <div class="px-4 py-3 border-b border-white/5 flex items-center space-x-2 overflow-x-auto no-scrollbar bg-[#1a1b1e]">
                <!-- Emoji Picker Toggle -->
                <button 
                    @click="showPicker = !showPicker"
                    class="h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-full border border-white/10 hover:bg-white/5 transition-all"
                    :class="showPicker ? 'bg-white/10 border-white/20' : ''"
                >
                   <EmojiHappyIcon class="h-6 w-6 text-gray-400" />
                </button>

                <!-- Quick React Buttons (Existing Emojis) -->
                <button 
                    v-for="emoji in uniqueEmojis" 
                    :key="emoji"
                    @click="$emit('react', emoji)"
                    class="px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center space-x-2 whitespace-nowrap border border-white/10"
                    :class="hasMyReaction(emoji) ? 'bg-white/10 text-white border-white/20' : 'text-gray-400 hover:text-gray-300'"
                >
                    <span>{{ emoji }}</span>
                    <span class="text-xs opacity-70">{{ getCountForEmoji(emoji) }}</span>
                </button>
            </div>

                <!-- Content Area -->
            <div class="max-h-[420px] overflow-y-auto custom-scrollbar bg-[#1a1b1e]">
                <!-- Emoji Picker -->
                <div v-if="showPicker" class="p-0 bg-[#1a1b1e]">
                    <EmojiPicker 
                        :native="true" 
                        theme="dark"
                        @select="onEmojiSelect"
                        :disable-skin-tones="true"
                        class="w-full !bg-transparent !border-0 !shadow-none !font-sans"
                    />
                </div>

                <!-- Unified User List (Shows ALL) -->
                <div v-else class="p-2 space-y-1">
                    <div 
                        v-for="r in reactions" 
                        :key="r.id"
                        @click="handleReactionClick(r)"
                        class="flex items-center justify-between p-3 rounded-2xl transition-all group"
                        :class="isMyReaction(r) ? 'bg-white/5 cursor-pointer hover:bg-white/10' : ''"
                    >
                        <div class="flex items-center space-x-4">
                            <img 
                                :src="r.user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(r.user_name)}&background=random`" 
                                class="h-11 w-11 rounded-full object-cover border border-white/10"
                                alt="User"
                            />
                            <div>
                                <p class="text-sm font-medium text-white">{{ isMyReaction(r) ? 'You' : r.user_name }}</p>
                                <p v-if="isMyReaction(r)" class="text-xs text-gray-500">Click to remove</p>
                            </div>
                        </div>
                        <span class="text-2xl">{{ r.emoji }}</span>
                    </div>
                    
                    <div v-if="reactions.length === 0 && !showPicker" class="py-12 text-center">
                        <p class="text-gray-500 text-sm italic">No reactions yet. Be the first!</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { EmojiHappyIcon } from "@heroicons/vue/outline";
import EmojiPicker from 'vue3-emoji-picker';
import 'vue3-emoji-picker/css';

export default {
    name: 'ReactionModal',
    components: { EmojiHappyIcon, EmojiPicker },
    props: {
        show: { type: Boolean, default: false },
        reactions: { type: Array, default: () => [] }
    },
    emits: ['close', 'remove', 'react'],
    data() {
        return {
            showPicker: false
        };
    },
    watch: {
        show(newVal) {
            if (newVal) {
                // Default to picker if no reactions, else show list
                this.showPicker = this.reactions.length === 0;
            }
        }
    },
    computed: {
        uniqueEmojis() {
            return [...new Set(this.reactions.map(r => r.emoji))];
        }
    },
    methods: {
        getCountForEmoji(emoji) {
            return this.reactions.filter(r => r.emoji === emoji).length;
        },
        isMyReaction(r) {
            return Number(r.user_id) === Number(this.$page.props.user.id);
        },
        hasMyReaction(emoji) {
            return this.reactions.some(r => r.emoji === emoji && Number(r.user_id) === Number(this.$page.props.user.id));
        },
        handleReactionClick(r) {
            if (this.isMyReaction(r)) {
                this.$emit('remove', r.emoji);
            }
        },
        onEmojiSelect(emoji) {
            this.$emit('react', emoji.i);
            this.showPicker = false;
        }
    }
};
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
    display: none;
}
.no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
.custom-scrollbar::-webkit-scrollbar {
    width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
}

/* Custom override for the emoji picker to make it feel premium */
:deep(.v3-emoji-picker) {
    background: #1a1b1e !important;
    font-family: inherit !important;
}

:deep(.v3-header) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
    padding: 12px !important;
}

:deep(.v3-input-search) {
    background: #2D3340 !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    color: white !important;
    border-radius: 12px !important;
    padding: 10px 16px !important;
}

:deep(.v3-groups) {
    padding: 8px 4px !important;
}

:deep(.v3-group-title) {
    background: #1a1b1e !important;
    color: #9ca3af !important;
    font-weight: 600 !important;
    font-size: 0.75rem !important;
    text-transform: uppercase !important;
    letter-spacing: 0.05em !important;
    padding: 12px 16px 8px !important;
}

:deep(.v3-emojis) {
    padding: 0 8px !important;
}

:deep(.v3-emoji) {
    border-radius: 8px !important;
    transition: all 0.2s !important;
}

:deep(.v3-emoji:hover) {
    background: rgba(255, 255, 255, 0.05) !important;
    transform: scale(1.15);
}

:deep(.v3-footer) {
    display: none !important;
}

:deep(.v3-tabs) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
    background: #1a1b1e !important;
}

:deep(.v3-tab) {
    filter: grayscale(1) opacity(0.5);
}

:deep(.v3-tab.v3-is-active) {
    filter: none opacity(1);
    border-bottom-color: #3b82f6 !important;
}
</style>
